import { type FC, useEffect } from "react";
import Lottie from "react-lottie";

import { usePerson } from "@/shared/hooks/useAppContext";
import { useQuickIdCheck } from "@/shared/hooks/useQuickIdCheck";
import { EventType, trackEvent } from "@/shared/utils/core";
import { getOnboardingFlow } from "@/shared/utils/funnelAnalytics";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import scanningAnimation from "@/shared/assets/animations/id-scanning.json";

export const IdVerifying: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  answers,
  setAnswer,
}) => {
  const person = usePerson();
  const estateId = person?.estate_id;
  const { runCheck } = useQuickIdCheck(estateId);

  useEffect(() => {
    const rawDocId = answers["id-document-id"];
    const documentId = Array.isArray(rawDocId) ? rawDocId[0] : rawDocId;
    if (!documentId || !estateId) {
      goToScreen("upload-id");
      return;
    }

    const trackResult = (verificationResult: "verified" | "failed") =>
      trackEvent(EventType.IDENTITY_VERIFICATION_COMPLETED, {
        flow: getOnboardingFlow(answers),
        estate_id: String(estateId),
        verification_result: verificationResult,
      });

    runCheck(parseInt(documentId, 10), {
      onVerified: () => {
        trackResult("verified");
        onComplete();
      },
      onSkipped: () => onComplete(),
      onRejected: () => {
        trackResult("failed");
        setAnswer("id-check-error", "not_id");
        goToScreen("upload-id");
      },
      onFailed: () => {
        trackResult("failed");
        setAnswer("id-check-error", "not_id");
        goToScreen("upload-id");
      },
    });
  }, [answers, estateId, goToScreen, onComplete, setAnswer, runCheck]);

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="min-h-100 w-80">
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData: scanningAnimation,
            rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
          }}
          isClickToPauseDisabled
        />
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <h3>Verifying your ID</h3>
        <p className="text-secondary">This usually takes about 20 seconds</p>
      </div>
    </div>
  );
};
